import { Box, Button, Card, Stack, Typography } from '@mui/material'
import { ProfileImage } from '../../customComponents/user/ProfileImage'
import { ArrowForward } from '@mui/icons-material'
import { type MouseEventHandler } from 'react'
import { ORG_AVATAR_COLORS, ORG_CARD_COLORS } from '../../../utils/colors'
import { AnnualPlanStatusChip } from '../../customComponents/AnnualPlanStatusChip'
import { type RcnPlanEntry } from '../../../models/requestObjects/AnnualPlanResponseObjects'

interface Props {
  name: string
  imageKey?: string
  buttonText: string
  onClick: MouseEventHandler<HTMLButtonElement>
  dataTestId?: string
  buttonDataTestId?: string
  index?: number
  disabled?: boolean
  // Active plans to show as chips. undefined = statuses not loaded (show nothing);
  // empty array = loaded but the RCN has no plan for this phase ("No active plan").
  plans?: RcnPlanEntry[]
}

export const OrgCard = (props: Props): JSX.Element => {
  const backgroundColor = ORG_CARD_COLORS[props.index == null ? props.name.charCodeAt(0) % ORG_CARD_COLORS.length : props.index % ORG_CARD_COLORS.length]
  const avatarColor = ORG_AVATAR_COLORS[props.index == null ? props.name.charCodeAt(0) % ORG_AVATAR_COLORS.length : props.index % ORG_AVATAR_COLORS.length]

  return (
    <Card data-testid={props?.dataTestId ?? 'org-card'} sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: '13.5px',
      alignItems: 'center',
      maxWidth: '285px',
      width: '100%',
      minHeight: '270px',
      borderRadius: '16px',
      padding: '24px',
      backgroundColor
    }}
    >
      <Typography
        variant='h4'
        width='100%'
        textOverflow='ellipsis'
        overflow='hidden'
        whiteSpace='nowrap'
        title={props.name}
      >{props.name}</Typography>

      <ProfileImage
        width='7.5em'
        height='7.5em'
        backgroundColor={avatarColor}
        name={props.name}
        imageKey={props.imageKey}
      />

      {props.plans != null && (
        props.plans.length === 0
          ? (
            <Box
              data-testid='no-active-plan'
              sx={{ padding: '6px 20px', borderRadius: '60px', backgroundColor: 'var(--grey-100)', color: 'var(--grey-700)' }}
            >
              <Typography variant='body1' sx={{ fontWeight: '600', whiteSpace: 'nowrap' }}>No active plan</Typography>
            </Box>
            )
          : (
            <Stack spacing='8px' alignItems='center' sx={{ width: '100%' }}>
              {props.plans.map(plan => (
                <AnnualPlanStatusChip key={plan.annualPlanId} status={plan.status} schoolYear={plan.schoolYear} />
              ))}
            </Stack>
            )
      )}

      <Button
        data-testid={props?.buttonDataTestId ?? 'org-card-button'}
        onClick={props.onClick}
        disabled={props.disabled}
        sx={[{
          padding: '12px 24px',
          backgroundColor: 'var(--white)',
          color: 'var(--grey-700)'
        },
        {
          '&:hover': {
            backgroundColor
          }
        }]
      }
      >
        {props.buttonText} <ArrowForward sx={{ ml: '10px' }} />
      </Button>
    </Card>
  )
}
